/**
 * 激励机制服务（XP / 等级 / 徽章 / 成就）
 * ------------------------------------------------------------
 * 设计原则：规则是数据，不是代码。
 *   - 经验值计算规则在 XP_RULES（可调参数）
 *   - 成就定义在 data/achievements.json（新增成就无需改代码）
 *   - 成就判定逻辑在下方 ACHIEVERS 里按 condition.type 注册
 *
 * 新增一种成就条件：在 ACHIEVERS 里加一个 key，对应 achievements.json 的 type。
 */
import { getStorage } from '../core/storage/index.js';
import { COLLECTIONS } from '../core/schema.js';
import { state, updateStudent, levelFromXp } from '../core/store.js';
import { makeAchievement } from '../core/models.js';
import { uid, clamp } from '../utils/index.js';
import { listSessions, studyStreak, unitMasteryRate } from './records.js';

/* ------------------------------ 经验值规则 ------------------------------ */
export const XP_RULES = {
  basePerCorrect: 5,        // 每题答对基础经验
  independentBonus: 2,      // 独立完成额外经验（没看提示）
  fullScoreBonus: 30,       // 满分额外奖励
  accuracyBonusAt: 80,      // 正确率达到该值额外奖励
  accuracyBonus: 15,
};

/** 单次练习可获得经验值 */
export function computeXP(summary) {
  const correct = summary.correct || 0;
  const independent = summary.independent || 0;
  let xp = correct * XP_RULES.basePerCorrect + independent * XP_RULES.independentBonus;
  if (summary.total && correct === summary.total) xp += XP_RULES.fullScoreBonus;
  if ((summary.accuracy || 0) >= XP_RULES.accuracyBonusAt) xp += XP_RULES.accuracyBonus;
  return Math.max(0, Math.round(xp));
}

/* ------------------------------ 成就数据 ------------------------------ */
let catalogCache = null;

export async function loadAchievementCatalog() {
  if (catalogCache) return catalogCache;
  if (typeof window !== 'undefined' && window.__INLINE_DATA__?.['data/achievements.json']) {
    catalogCache = window.__INLINE_DATA__['data/achievements.json'];
    return catalogCache;
  }
  const url = new URL('data/achievements.json', document.baseURI).href;
  const res = await fetch(url, { cache: 'no-cache' });
  if (!res.ok) throw new Error('成就配置加载失败');
  catalogCache = await res.json();
  return catalogCache;
}

export async function earnedAchievements() {
  const storage = getStorage();
  const rows = await storage.list(COLLECTIONS.ACHIEVEMENTS);
  return rows.filter(a => a.studentId === state.student?.id);
}

/** 计算可发放的成就（不写库） */
async function evaluate(unlockedCodes) {
  const catalog = await loadAchievementCatalog();
  const sessions = await listSessions();
  const streak = await studyStreak();
  const totalSessions = sessions.length;

  const byUnit = new Map();
  sessions.forEach(s => {
    if (!s.unitId) return;
    const u = byUnit.get(s.unitId) || { count: 0, best: 0, full: 0 };
    u.count += 1;
    u.best = Math.max(u.best, s.score || 0);
    if (s.score === 100) u.full += 1;
    byUnit.set(s.unitId, u);
  });

  const moduleAcc = mod => {
    const rows = sessions.filter(s => s.module === mod);
    if (rows.length < 3) return null;
    const total = rows.reduce((n, s) => n + (s.total || 0), 0);
    const correct = rows.reduce((n, s) => n + (s.correct || 0), 0);
    return total ? Math.round(correct / total * 100) : 0;
  };

  const perfection = sessions.filter(s => s.total > 0 && s.correct === s.total).length;
  const gameCount = sessions.filter(s => s.module === 'game').length;

  /** 条件判定器：key = condition.type */
  const ACHIEVERS = {
    total_sessions: c => totalSessions >= c.n,
    streak_days: c => streak >= c.n,
    perfection_count: c => perfection >= c.n,
    game_count: c => gameCount >= c.n,
    module_count: c => sessions.filter(s => s.module === c.module).length >= c.n,
    word_accuracy: c => (moduleAcc('word') ?? -1) >= c.n,
    sentence_accuracy: c => (moduleAcc('sentence') ?? -1) >= c.n,
    grammar_accuracy: c => (moduleAcc('grammar') ?? -1) >= c.n,
    unit_session_count: c => [...byUnit.values()].some(u => u.count >= c.n),
    unit_best_score: c => [...byUnit.values()].some(u => u.best >= c.n),
    unit_full_score: c => [...byUnit.values()].some(u => u.full > 0),
    unit_mastered: async c => {
      for (const unitId of byUnit.keys()) {
        const rate = await unitMasteryRate(unitId);
        if (rate * 100 >= c.n) return true;
      }
      return false;
    },
  };

  const newly = [];
  const skipped = [];
  for (const def of catalog.achievements || []) {
    if (unlockedCodes.has(def.code)) continue;
    const fn = ACHIEVERS[def.condition?.type];
    if (!fn) { skipped.push(`${def.code}: 未实现的 condition.type=${def.condition?.type}`); continue; }
    try {
      const ok = await fn(def.condition);
      if (ok) newly.push(def);
    } catch (err) {
      console.warn('[rewards] 成就判定异常', def.code, err);
      skipped.push(`${def.code}: ${err.message}`);
    }
  }
  if (skipped.length) console.info('[rewards] 本次未通过的成就', skipped.length, skipped.slice(0, 5));
  return newly;
}

/** 判定并发放成就，返回本次新解锁的成就（含 xp 奖励） */
export async function checkAchievements() {
  const storage = getStorage();
  const existing = await earnedAchievements();
  const codes = new Set(existing.map(a => a.code));
  const newly = await evaluate(codes);
  if (!newly.length) return [];

  const rows = newly.map(def => makeAchievement({
    id: uid('ach'),
    studentId: state.student.id,
    code: def.code,
    unlockedAt: new Date().toISOString(),
  }));
  await storage.bulkPut(COLLECTIONS.ACHIEVEMENTS, rows);

  const bonusXp = newly.reduce((n, d) => n + (d.xp || 0), 0);
  if (bonusXp) await updateStudent({ xp: (state.student.xp || 0) + bonusXp });

  return newly.map(d => ({ ...d, xp: d.xp || 0 }));
}

/**
 * 结算一次练习：写 XP + 判成就
 * @param {object} summary  PracticeSession.summary()
 * @returns {Promise<{xp:number, level:object, unlocked:object[], student:object}>}
 */
export async function settleSession(summary) {
  const xp = computeXP(summary);
  summary.xpEarned = xp;
  if (xp) await updateStudent({ xp: (state.student.xp || 0) + xp });
  const unlocked = await checkAchievements();
  return {
    xp,
    level: levelFromXp(state.student?.xp || 0),
    unlocked,
    student: state.student,
  };
}

/** 徽章墙数据：全部成就定义 + 是否已获得 */
export async function achievementBoard() {
  const catalog = await loadAchievementCatalog();
  const got = await earnedAchievements();
  const gotMap = new Map(got.map(a => [a.code, a]));
  return (catalog.achievements || []).map(def => ({
    ...def,
    unlocked: gotMap.has(def.code),
    unlockedAt: gotMap.get(def.code)?.unlockedAt || null,
  }));
}

/** 每 100 XP 一级的进度信息 */
export function levelInfo() {
  return levelFromXp(state.student?.xp || 0);
}

/** 进度条百分比（0~100） */
export const clampPercent = n => clamp(Math.round(n), 0, 100);
